
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import Terminal from 'plaid-threads/Icons/Terminal';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Enhance conversion by embedding institution search directly into your app.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link Embedded Institution Search' subtitle='Enhance uptake of Plaid by embedding institution search directly into your app' mdxType="Header" />
    <p>{`With Embedded Institution Search (also known as Embedded Link), you can provide a more seamless transition to the account connectivity experience and increase the percentage of users who choose Plaid. For payment experiences in particular, embedding institution search can help guide users to choosing their bank account as a payment method.`}</p>
    <YouTubeEmbed videoId='TqUo9z-r3x8' alignment='full' title='Embedded Link - Quick Tip' mdxType="YouTubeEmbed" />
    <p>{`Embedded Institution Search and `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/database-auth"
      }}>{`Database Auth`}</a>{` are both designed to increase adoption of ACH payment methods and are frequently used together. Embedded Institution Search is also fully compatible with other Auth flows, including micro-deposit based flows.`}</p>
    <h4 {...{
      "id": "who-should-use-embedded-institution-search"
    }}>{`Who should use Embedded Institution Search`}</h4>
    <p>{`Embedded Institution Search is recommended when:`}</p>
    <ul>
      <li parentName="ul">{`You want to encourage your user to use Plaid, either because Plaid is your only flow, or because you benefit when the user chooses Plaid over an alternative (e.g. pay-by-bank flows).`}</li>
      <li parentName="ul">{`Your design can accommodate a Plaid embed of at least `}<strong parentName="li">{`350x300px or 300x350px`}</strong>{`. `}</li>
      <li parentName="ul">{`You are not using Multi-Item Link.`}</li>
      <li parentName="ul">{`You are not displaying Plaid Link within a webview or iFrame.`}</li>
    </ul>
    <p>{`Embedded Institution Search can be used with the following products: Auth, Identity, Balance, Signal, Transfer, Transactions, Investments, Assets, Liabilities, and all Plaid Check Consumer Report products.`}</p>
    <p>{`Embedded Institution Search is not compatible with `}<a parentName="p" {...{
        "href": "/docs/link/multi-item-link/"
      }}>{`Multi-Item Link`}</a>{`. `}</p>
    <h5 {...{
      "id": "embedded-institution-search-for-pay-by-bank-flows"
    }}>{`Embedded Institution Search for pay-by-bank flows`}</h5>
    <p>{`It is highly recommended to use Embedded Institution Search for Link to increase uptake of pay-by-bank if your use case involves a pay-by-bank payments flow where the end user can choose between paying via a credit card and paying via a bank account. If your use case is an account opening or funding flow that requires the customer to use a bank account, or has a surcharge for credit card use, use the standard Link experience instead.`}</p>
    <p>{`In order to realize the benefits of Embedded Institution Search, you must show it by default when the payments view loads, without requiring the user to first select 'Pay by bank'. If you cannot do this, use the standard Link experience instead.`}</p>
    <Callout mdxType="Callout">
      <p>{`For recommendations on how to improve the uptake of pay-by-bank and optimize your Embedded Institution Search UX, see `}<a parentName="p" {...{
          "href": "/docs/auth/pay-by-bank-ux"
        }}>{`Increasing Pay-by-bank adoption`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "institution-search-user-experience"
    }}>{`Institution Search user experience`}</h4>
    <p>{`The user will be able to select from a set of institution logos. This set of logos is customized based on user data, such as location. Alternatively, you can choose which logos will be displayed via the Plaid Dashboard. If a user selects an institution, they will be taken to Link to connect their account.`}</p>
    <Image src='/assets/img/docs/embedded-search-desktop.png' alt="Payment options screen with monthly plan selected, pay by bank method, search bar, and bank logos. Pay button at the bottom." caption='Example of Embedded Institution Search on Desktop' expandable={true} mdxType="Image" />
    <Image src='/assets/img/docs/embedded-search-mobile.png' alt="Mobile flow showing institution search, log in to Gingham Bank, and success confirmation via Plaid for payment setup." caption='Example of Embedded Institution Search flow on mobile when user selects an institution' expandable={true} mdxType="Image" />
    <p>{`If the user's institution is not one of the featured institutions, they can search for their institution using the search bar.`}</p>
    <Image src='/assets/img/docs/embedded-search-search-bar.gif' alt="Search for a bank in the checkout payment method section using the search bar or select from listed institutions; option to connect manually." caption='Example of searching for an institution in Embedded Institution Search on desktop' mdxType="Image" />
    <h4 {...{
      "id": "returning-user-experience"
    }}>{`Returning user experience`}</h4>
    <p>{`The Embedded Institution Search experience can be further optimized for users who have already connected a financial account with Plaid.`}</p>
    <Image src='/assets/img/docs/embedded-search-returning-user.gif' alt="Returning user form in Embedded Institution Search with fields for name, address, city, state, and zip code, and a 'Continue' button." caption='Example of the returning user experience in Embedded Institution Search' expandable={true} mdxType="Image" />
    <p>{`When a user's device is recognized or the phone number is provided via `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, Plaid will check to see if they are a returning user. If they are detected as a returning user, Link will run a security check on the session in the background. If this check passes, the user will be shown a list of previously connected accounts in the embedded module.`}</p>
    <p>{`The user can then verify their phone number using a one-time password and complete the account linking process if the previously linked institutions do not require re-authentication. For more details on the returning user flow, see `}<a parentName="p" {...{
        "href": "/docs/link/returning-user"
      }}>{`Returning user experience`}</a>{`.`}</p>
    <p>{`Note that if Plaid Check Consumer Report products are included in the Link token request, then returning users will see the default Institution Search user experience.`}</p>
    <h5 {...{
      "id": "testing-in-sandbox"
    }}>{`Testing in Sandbox`}</h5>
    <p>{`In order to test the returning user experience in Embedded Institution Search, use the phone numbers listed in `}<a parentName="p" {...{
        "href": "/docs/link/returning-user/#testing-in-sandbox"
      }}>{`Testing returning user experience in Sandbox`}</a>{`.`}</p>
    <h4 {...{
      "id": "integration-steps"
    }}>{`Integration steps`}</h4>
    <p>{`Embedded Institution Search is available for all supported integration modes except webviews and Hosted Link. `}</p>
    <Callout warning mdxType="Callout">
      <p>{`Embedded Institution Search cannot be rendered inside an iFrame.`}</p>
    </Callout>
    <p>{`Before integrating, make sure you are using a version of the SDK that supports Embedded Institution Search.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Platform`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Minimum SDK version required`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/plaid/plaid-link-android"
            }}>{`Android`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`3.14.0`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/plaid/plaid-link-ios"
            }}>{`iOS`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`4.5.0`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/plaid/react-native-plaid-link-sdk"
            }}>{`React Native`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`10.6.0`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/plaid/plaid-link-flutter"
            }}>{`Flutter`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`1.0.0`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/plaid/react-plaid-link"
            }}>{`React web`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`3.5.1`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`JavaScript web`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A (all customers are on the latest version)`}</td>
        </tr>
      </tbody>
    </table>
    <ol>
      <li parentName="ol">{`Create a Link token as normal, using `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`; no special parameters are required when making this call to enable Embedded Institution Search. The 'Connect Manually' button rendering is configured with the `}<inlineCode parentName="li">{`auth_type_select_enabled`}</inlineCode>{` boolean in the `}<inlineCode parentName="li">{`auth`}</inlineCode>{` object.`}</li>
      <li parentName="ol">{`Create an embedded view. This should be done before it will be displayed in your app, to minimize latency. Plaid will track when this view is requested and activate the Embedded Search UI.
If you are using iOS, call `}<inlineCode parentName="li">{`Plaid.createEmbeddedLinkView(configuration:)`}</inlineCode>{` for SwiftUI or `}<inlineCode parentName="li">{`Plaid.createEmbeddedLinkUIView(configuration:presentationMethod:)`}</inlineCode>{` for UIKit. Once you have the view, add it to your layout.
If you are using the web SDK, call `}<inlineCode parentName="li">{`Plaid.createEmbedded`}</inlineCode>{` instead of `}<inlineCode parentName="li">{`Plaid.create`}</inlineCode>{` to open Link.
For other platforms, you will create the view as normal.`}</li>
      <li parentName="ol">{`Lay out the view Plaid returns from the configuration. Using a `}<strong parentName="li">{`minimum size of 350x300px or 300x350px`}</strong>{` is strongly recommended. Smaller sizes will significantly degrade conversion. If you are migrating to Embedded Institution Search from traditional Link, delete your old Link presentation in favor of this new one.`}</li>
    </ol>
    <ContentSwitcher.Container id='embeddedLink' names={['iOS', 'Android', 'JavaScript', 'React']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <ContentSwitcher.Container id='embeddedLink-ios' names={['SwiftUI', 'UIKit']} hiddenIsSearchable={true}>
          <ContentSwitcher.Content>
            <pre><code parentName="pre" {...{
                "className": "language-swift",
                "metastring": "switcher=false #Embedded Institution Search - iOS (SwiftUI)",
                "switcher": "false",
                "#Embedded": true,
                "Institution": true,
                "Search": true,
                "-": true,
                "iOS": true,
                "(SwiftUI)": true
              }}>{`struct EmbeddedInstitutionSearchView: View {
    let linkToken: String
    @State private var embeddedSearchView: EmbeddedSearchView?

    var body: some View {
        VStack {
            if let embeddedSearchView {
                embeddedSearchView
            }
        }
        .onAppear(perform: createEmbeddedSearchView)
    }

    private func createEmbeddedSearchView() {
        let configuration = EmbeddedLinkTokenConfiguration(
            token: linkToken,
            onSuccess: { success in
                print("success: \\(success)")
            },
            onExit: { exit in
                print("exit: \\(exit)")
            },
            onEvent: { event in
                print("event: \\(event)")
            }
        )

        do {
            embeddedSearchView = try Plaid.createEmbeddedLinkView(configuration: configuration)
        } catch {
            // Handle the error.
        }
    }
}
`}</code></pre>
          </ContentSwitcher.Content>
          <ContentSwitcher.Content>
            <pre><code parentName="pre" {...{
                "className": "language-swift",
                "metastring": "switcher=false #Embedded Institution Search - iOS (UIKit)",
                "switcher": "false",
                "#Embedded": true,
                "Institution": true,
                "Search": true,
                "-": true,
                "iOS": true,
                "(UIKit)": true
              }}>{`let configuration = EmbeddedLinkTokenConfiguration(
    token: token,
    onSuccess: { success in
        print("success: \\(success)")
    },
    onExit: { exit in
        print("exit: \\(exit)")
    },
    onEvent: { event in
        print("event: \\(event)")
    }
)

let embeddedSearchView: EmbeddedSearchUIView = try Plaid.createEmbeddedLinkUIView(
    configuration: configuration,
    presentationMethod: .viewController(self)
)

embeddedSearchView.translatesAutoresizingMaskIntoConstraints = false
view.addSubview(embeddedSearchView)

NSLayoutConstraint.activate([
    embeddedSearchView.topAnchor.constraint(equalTo: view.safeAreaLayoutGuide.topAnchor, constant: 8),
    embeddedSearchView.leadingAnchor.constraint(equalTo: view.leadingAnchor, constant: 25),
    embeddedSearchView.trailingAnchor.constraint(equalTo: view.trailingAnchor, constant: -25),
    embeddedSearchView.heightAnchor.constraint(equalToConstant: embeddedSearchView.expectedHeight),
])
`}</code></pre>
          </ContentSwitcher.Content>
        </ContentSwitcher.Container>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-kotlin",
            "metastring": "switcher=false #Embedded Institution Search - Android (Kotlin)",
            "switcher": "false",
            "#Embedded": true,
            "Institution": true,
            "Search": true,
            "-": true,
            "Android": true,
            "(Kotlin)": true
          }}>{`// Register a callback for an Activity Result like normal (must be done from an Activity)
 private val linkAccountToPlaid =
   registerForActivityResult(OpenPlaidLink()) { result ->
      when (result) {
        is LinkSuccess -> /* handle LinkSuccess */
        is LinkExit -> /* handle LinkExit (from LinkActivity) */
      }
    }

// Create an EmbeddedLinkTokenConfiguration, handling exits from the embedded view via onEmbeddedViewExit
val embeddedConfiguration = EmbeddedLinkTokenConfiguration.Builder()
  .token(token)
  .onEmbeddedViewExit { exit: LinkExit -> /* handle LinkExit (from Embedded View) */ }
  .build()

// Selecting an institution hands off to full Link, launched through the launcher you pass in
val embeddedView = Plaid.createPlaidEmbeddedLinkView(
this /*Activity context*/,
embeddedConfiguration,
linkAccountToPlaid,
)

// Add this embeddedView to a view in your layout
binding.embeddedViewContainer.addView(embeddedView)
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-html",
            "metastring": "switcher=false #Embedded Institution Search - web (HTML)",
            "switcher": "false",
            "#Embedded": true,
            "Institution": true,
            "Search": true,
            "-": true,
            "web": true,
            "(HTML)": true
          }}>{`<div id="plaid-embedded-link-container"></div>
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #Embedded Institution Search - web (JavaScript)",
            "switcher": "false",
            "#Embedded": true,
            "Institution": true,
            "Search": true,
            "-": true,
            "web": true,
            "(JavaScript)": true
          }}>{`// The container at \`#plaid-embedded-link-container\` will need to be sized in order to
// control the size of the embedded Plaid module
const embeddedLinkOpenTarget = document.querySelector('#plaid-embedded-link-container');

Plaid.createEmbedded(
    {
      token: 'GENERATED_LINK_TOKEN',
      onSuccess: (public_token, metadata) => {},
      onLoad: () => {},
      onExit: (err, metadata) => {},
      onEvent: (eventName, metadata) => {},
    },
    embeddedLinkOpenTarget,
);
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-tsx",
            "metastring": "switcher=false #Embedded Institution Search - web (React)",
            "switcher": "false",
            "#Embedded": true,
            "Institution": true,
            "Search": true,
            "-": true,
            "web": true,
            "(React)": true
          }}>{`import React, { useCallback } from 'react';
import { PlaidEmbeddedLink } from 'react-plaid-link';

const App = props => {
  const onSuccess = useCallback(
    (token, metadata) => console.log('onSuccess', token, metadata),
    []
  );

  const onEvent = useCallback(
    (eventName, metadata) => console.log('onEvent', eventName, metadata),
    []
  );

  const onExit = useCallback(
    (err, metadata) => console.log('onExit', err, metadata),
    []
  );

  const config = {
    token: "plaid-token-123",
    onSuccess,
    onEvent,
    onExit,
  };

  return (
    <PlaidEmbeddedLink
      {...config}
      style={{
        height: '350px',
        width: '350px',
      }}
    />
  );
};

export default App;
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <Callout icon={<Terminal mdxType="Terminal" />} mdxType="Callout">
      <p>{`For an end-to-end example app, see the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/transfer-quickstart/"
        }}>{`Transfer Quickstart`}</a>{`, which incorporates Embedded Institution Search in a JavaScript frontend.`}</p>
    </Callout>
    <h5 {...{
      "id": "update-mode"
    }}>{`Update mode`}</h5>
    <p>{`Embedded Institution Search cannot be used in `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`; to update a user's Item, launch a regular update mode session instead.`}</p>
    <h4 {...{
      "id": "customization-options"
    }}>{`Customization options`}</h4>
    <p>{`You can customize the Embedded Institution Search user experience to match your application's needs.`}</p>
    <h5 {...{
      "id": "module-responsiveness-and-tile-count"
    }}>{`Module responsiveness and tile count`}</h5>
    <p>{`The embedded Link module will responsively scale to display between two and fifteen institutions, depending on the height and width of the module. Using a `}<strong parentName="p">{`minimum size of 350x300px or 300x350px`}</strong>{` is strongly recommended, as smaller sizes degrade conversion particularly on returning user flows. Testing both embedded search and returning user experiences at your chosen module size to ensure usability is also strongly recommended.`}</p>
    <p>{`On the web, the embedded Plaid module will attempt to use 100% of the height and width of its container. To modify the size of the Plaid module, or to render the Plaid module responsively, you should set the size of the container.`}</p>
    <Callout mdxType="Callout">
      <p>{`Plaid recommends using the largest container that will comfortably fit in your UI. The larger the container, the more institution logos will be shown. Users are more likely to adopt pay-by-bank when they see their institution's logo on the embedded Link screen.`}</p>
    </Callout>
    <h5 {...{
      "id": "customizing-institutions-displayed"
    }}>{`Customizing institutions displayed`}</h5>
    <p>{`The institutions displayed in Link Embedded Search are based on your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link/institution-select"
      }}>{`Institution Select settings`}</a>{`, which you can optionally customize in the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link/institution-select"
      }}>{`Dashboard`}</a>{`. For most customers, it is recommended to use the default settings.`}</p>
    <p>{`Embedded Institution Search is compatible with the `}<a parentName="p" {...{
        "href": "/docs/link/customization/#institution-select-shortcut"
      }}>{`Institution Select Shortcut`}</a>{`: If you already know which institution the user wants to connect to before initializing Link, you can pass `}<inlineCode parentName="p">{`routing_number`}</inlineCode>{` into the `}<inlineCode parentName="p">{`institution_data`}</inlineCode>{` request field in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. The matched institution will be listed first (top left position) in the embedded institution grid.`}</p>
    <h4 {...{
      "id": "event-callbacks-emitted-by-embedded-institution-search"
    }}>{`Event callbacks emitted by Embedded Institution Search`}</h4>
    <h6 {...{
      "id": "user-chooses-an-institution-directly-from-embedded-search"
    }}>{`User chooses an institution directly from embedded search`}</h6>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`onEvent: OPEN`}</inlineCode>{` – `}<inlineCode parentName="li">{`view_name:  "CONSENT"`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`onEvent: SELECT_INSTITUTION`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`onEvent: TRANSITION_VIEW`}</inlineCode>{` – `}<inlineCode parentName="li">{`view_name:  "CONSENT"`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`onEvent: TRANSITION_VIEW`}</inlineCode>{` – `}<inlineCode parentName="li">{`view_name: "CREDENTIAL" or "OAUTH"`}</inlineCode>{` - user selects Continue on the Consent pane`}</li>
    </ul>
    <p>{`Note that viewing the Embedded Institution Search pane will not result in any Link events or analytics activity. Only when the end user interacts with Embedded Institution Search will events appear. `}</p>
    <h4 {...{
      "id": "ui-recommendations-for-embedded-institution-search"
    }}>{`UI recommendations for Embedded Institution Search`}</h4>
    <p>{`See `}<a parentName="p" {...{
        "href": "/docs/auth/pay-by-bank-ux"
      }}>{`Increasing pay-by-bank adoption`}</a>{` for recommendations on displaying Embedded Institution Search within your app for pay-by-bank use cases.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "who-should-use-embedded-institution-search",
        level: 4,
        title: "Who should use Embedded Institution Search",
      },
{
        id: "institution-search-user-experience",
        level: 4,
        title: "Institution Search user experience",
      },
{
        id: "returning-user-experience",
        level: 4,
        title: "Returning user experience",
      },
{
        id: "integration-steps",
        level: 4,
        title: "Integration steps",
      },
{
        id: "customization-options",
        level: 4,
        title: "Customization options",
      },
{
        id: "event-callbacks-emitted-by-embedded-institution-search",
        level: 4,
        title: "Event callbacks emitted by Embedded Institution Search",
      },
{
        id: "ui-recommendations-for-embedded-institution-search",
        level: 4,
        title: "UI recommendations for Embedded Institution Search",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
